import React, { useEffect } from 'react'
import AboutFirst from '../Components/About/AboutFirst'
import Mission from '../Components/About/Mission'
import FeaturedGames from '../Components/About/FeaturesGame'
import AboutHeroSection from '../Components/About/AboutHerosection'
import { useSEO } from './Seo'
import { Helmet } from 'react-helmet-async' 
import { Gamepad2, Globe, Shield, Zap } from 'lucide-react'

const About = () => {

useSEO({
    title: "About Game Zone Hub | Play Free Online Games",
    description:
      "Learn about Game Zone Hub, your ultimate destination to play free online games. Discover our mission to provide the best HTML5 browser games with no downloads required.",
    keywords:
      "about game zone hub, play free online games, online games, play free online, free browser games, html5 games platform, no download games",
    canonical: "https://www.gamezonehub.in/about",
    ogTitle: "About Game Zone Hub - Play Free Online Games",
    ogDescription: "Discover our mission to provide the best free online games instantly. No downloads, just play!",
  });


const STORAGE_KEY = "gameListPage"

useEffect(()=>{
  localStorage.setItem(STORAGE_KEY, String(1))
  window.scrollTo({top:"0"})
},[])

const schemaData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Game Zone Hub",
    "url": "https://www.gamezonehub.in/",
    "logo": "https://www.gamezonehub.in/logo.png",
    "description": "Game Zone Hub is a leading digital entertainment platform dedicated to providing high-quality, free online browser games.",
    "foundingDate": "2024", 
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer support",
      "email": "support@gamezonehub.in" 
    },
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 via-black to-gray-800 min-h-screen">
      <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>

      <AboutFirst/>
      <AboutHeroSection/>
      <Mission/>
      <FeaturedGames/>

      {/* 🚀 MASSIVELY EXPANDED SEO CONTENT BLOCK FOR ABOUT PAGE */}
      <section className="bg-[#0b1120] border-t border-white/10 sm:pt-20 pt-10 sm:pb-20 pb-10 px-4" data-aos="fade-up">
        <div className="container px-4 mx-auto space-y-16">
          
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-extrabold text-white mb-6">The Story Behind Game Zone Hub</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto rounded-full mb-8"></div>
            <p className="text-gray-400 max-w-4xl mx-auto text-lg leading-relaxed">
              We believe that high-quality digital entertainment should be instantly accessible to everyone, anywhere in the world, without frustrating barriers. Game Zone Hub was built by gamers, for gamers.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            
            {/* Left Column */}
            <div className="space-y-8 text-gray-300 leading-relaxed text-sm sm:text-base">
              
              <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-3">
                      <Gamepad2 className="text-cyan-400 w-8 h-8" /> Who We Are
                  </h3>
                  <p className="mb-4">
                    Founded with a deep passion for accessible entertainment, <strong>Game Zone Hub</strong> was created to revolutionize the way people <strong>play free online games</strong>. Over the years, we noticed a growing frustration among casual and hardcore gamers alike: the internet was becoming saturated with hidden paywalls, massive multi-gigabyte download requirements, and intrusive software installations that slowed down devices.
                  </p>
                  <p>
                    We decided to build a platform that completely eliminates these barriers. Our dedicated team of gaming enthusiasts, UI/UX designers, and web developers works tirelessly to curate a massive, ever-expanding library of premium HTML5 browser games. Whether you are looking to kill time during a quick 5-minute coffee break, challenge your friends in intense multiplayer arenas, or dive deep into a complex logic puzzle, our platform ensures that your next gaming session is just one click away.
                  </p>
              </div>

              <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-3">
                      <Globe className="text-blue-400 w-8 h-8" /> What We Offer
                  </h3>
                  <p>
                    Our catalog spans across dozens of popular genres. We host adrenaline-pumping 3D racing simulators, reflex-testing arcade shooters, relaxing dress-up and simulation games, and highly strategic board games. By partnering with talented independent developers and renowned game studios globally, we guarantee that our users always have access to fresh, top-tier content.
                  </p>
              </div>

            </div>

            {/* Right Column */}
            <div className="space-y-8 text-gray-300 leading-relaxed text-sm sm:text-base">
              
              <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-3">
                      <Zap className="text-yellow-400 w-8 h-8" /> Technological Edge
                  </h3>
                  <p className="mb-4">
                    We firmly believe that high-quality gaming should be universal. That is why every single title on Game Zone Hub is 100% free to play, forever. We utilize state-of-the-art cloud delivery networks and advanced WebGL/HTML5 frameworks to guarantee that our <strong>free browser games</strong> run flawlessly across all modern hardware.
                  </p>
                  <p>
                    Because the game rendering happens natively within your browser, you experience zero loading delays and buttery-smooth 60 FPS performance on high-end desktop PCs, laptops, Apple iPads, and Android smartphones. Our website's responsive architecture dynamically adapts the game's controls to fit your screen size perfectly, offering intuitive touch-swipe support for mobile users and precise keyboard mapping for desktop players.
                  </p>
              </div>

              <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-3">
                      <Shield className="text-green-400 w-8 h-8" /> Uncompromising Safety
                  </h3>
                  <p>
                    Digital safety and user experience are at the absolute core of our operations. We rigorously test, verify, and moderate our entire catalog to ensure it is completely free from malware, trojans, viruses, and inappropriate content. Unlike shady download sites, our games operate in a secure, sandboxed web environment. This strict quality control provides a secure, family-friendly space for gamers of all ages to explore, compete, and have fun without worrying about their device's security.
                  </p>
              </div>

            </div>

          </div>

          <div className="text-center bg-gradient-to-r from-blue-900/30 to-purple-900/30 p-10 rounded-3xl border border-white/10 mt-16 shadow-2xl">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">Join Our Growing Community</h3>
            <p className="text-gray-400 mb-0 max-w-3xl mx-auto text-sm sm:text-base leading-relaxed">
              Game Zone Hub is more than just a website; it is a global community of players who love great games. We are constantly evolving, listening to player feedback, and adding new features to enhance your experience. Thank you for making us your number one choice for online entertainment.
            </p>
          </div>

        </div>
      </section>

    </div>
  )
}

export default About